/**
 * French Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const fr: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Tableau de bord',
  'nav.funds': 'Fonds',
  'nav.investments': 'Investissements',
  'nav.investees': 'Entreprises',
  'nav.esg': 'Revue ESG',
  'nav.compliance': 'Conformit\u00e9',
  'nav.reports': 'Rapports',
  'nav.settings': 'Param\u00e8tres',
  'nav.users': 'Utilisateurs',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'Indicateurs PAI',
  'nav.taxonomy': 'Taxonomie UE',
  'nav.filings': 'D\u00e9clarations',
  'nav.calendar': 'Calendrier',
  'nav.submit': 'Soumettre les donn\u00e9es',
  'nav.submissions': 'Mes soumissions',
  'nav.benchmarks': 'R\u00e9f\u00e9rences',
  'nav.messages': 'Messages',
  'nav.portfolio': 'Portefeuille',
  'nav.performance': 'Performance des fonds',
  'nav.esgSummary': 'R\u00e9sum\u00e9 ESG',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': "M\u00e9triques d'impact",
  'nav.sdg': 'Cartographie ODD',
  'nav.audit': "Piste d'audit",
  'nav.snapshots': 'Instantan\u00e9s',
  'nav.knowledgeGraph': 'Graphe de Connaissances',
  'nav.geographic': 'G\u00e9ographique',
  'nav.bi': 'Studio BI',
  'nav.ai': 'Assistant IA',
  'nav.tasks': 'T\u00e2ches',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Moniteur R\u00e9glementaire',

  // Common Actions
  'action.create': 'Cr\u00e9er',
  'action.edit': 'Modifier',
  'action.delete': 'Supprimer',
  'action.save': 'Enregistrer',
  'action.cancel': 'Annuler',
  'action.submit': 'Soumettre',
  'action.approve': 'Approuver',
  'action.reject': 'Rejeter',
  'action.download': 'T\u00e9l\u00e9charger',
  'action.export': 'Exporter',
  'action.import': 'Importer',
  'action.filter': 'Filtrer',
  'action.search': 'Rechercher',
  'action.refresh': 'Actualiser',
  'action.close': 'Fermer',
  'action.back': 'Retour',
  'action.next': 'Suivant',
  'action.previous': 'Pr\u00e9c\u00e9dent',
  'action.viewAll': 'Voir tout',
  'action.viewDetails': 'Voir les d\u00e9tails',
  'action.generate': 'G\u00e9n\u00e9rer',
  'action.signOut': 'Se d\u00e9connecter',
  'action.addNew': 'Ajouter',
  'action.invite': 'Inviter',

  // Statuses
  'status.draft': 'Brouillon',
  'status.submitted': 'Soumis',
  'status.validated': 'Valid\u00e9',
  'status.approved': 'Approuv\u00e9',
  'status.rejected': 'Rejet\u00e9',
  'status.pending': 'En attente',
  'status.inProgress': 'En cours',
  'status.overdue': 'En retard',
  'status.compliant': 'Conforme',
  'status.filed': 'D\u00e9pos\u00e9',
  'status.active': 'Actif',
  'status.inactive': 'Inactif',
  'status.closed': 'Cl\u00f4tur\u00e9',
  'status.fundraising': 'Lev\u00e9e de fonds',
  'status.investing': 'Investissement',
  'status.harvesting': 'R\u00e9colte',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Vue d\'ensemble du portefeuille',
  'dashboard.complianceStatus': '\u00c9tat de conformit\u00e9',
  'dashboard.collectionProgress': 'Progression de la collecte',
  'dashboard.activityFeed': 'Activit\u00e9',
  'dashboard.quickActions': 'Actions rapides',
  'dashboard.investmentPipeline': 'Pipeline d\'investissement',
  'dashboard.totalFunds': 'Total des fonds',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Investissements actifs',
  'dashboard.totalInvestees': 'Entreprises',
  'dashboard.recentActivity': 'Activit\u00e9 r\u00e9cente',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Total engag\u00e9',
  'lp.capitalCalled': 'Capital appel\u00e9',
  'lp.distributed': 'Distribu\u00e9',
  'lp.netPosition': 'Position nette',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Performance des fonds',
  'lp.esgSummary': 'R\u00e9sum\u00e9 ESG',
  'lp.reportLibrary': 'Biblioth\u00e8que de rapports',
  'lp.taxonomyAligned': 'Align\u00e9 \u00e0 la taxonomie',
  'lp.paiCoverage': 'Couverture PAI',
  'lp.portfolioPerformance': 'Performance du portefeuille LP',

  // Fund Labels
  'fund.name': 'Nom du fonds',
  'fund.code': 'Code du fonds',
  'fund.vintage': 'Ann\u00e9e de cr\u00e9ation',
  'fund.size': 'Taille du fonds',
  'fund.targetSize': 'Taille cible',
  'fund.currency': 'Devise',
  'fund.strategy': 'Strat\u00e9gie',
  'fund.status': 'Statut',
  'fund.sfdrCategory': 'Cat\u00e9gorie SFDR',
  'fund.geographyFocus': 'Zone g\u00e9ographique',
  'fund.sectorFocus': 'Secteur cible',

  // Form Labels
  'form.name': 'Nom',
  'form.email': 'E-mail',
  'form.country': 'Pays',
  'form.amount': 'Montant',
  'form.currency': 'Devise',
  'form.date': 'Date',
  'form.description': 'Description',
  'form.notes': 'Notes',
  'form.type': 'Type',
  'form.period': 'P\u00e9riode',
  'form.year': 'Ann\u00e9e',
  'form.required': 'Obligatoire',
  'form.optional': 'Facultatif',

  // Table Headers
  'table.fund': 'Fonds',
  'table.commitment': 'Engagement',
  'table.called': 'Appel\u00e9',
  'table.distributed': 'Distribu\u00e9',
  'table.tvpi': 'TVPI',
  'table.status': 'Statut',
  'table.regulation': 'R\u00e9glementation',
  'table.filingType': 'Type de d\u00e9claration',
  'table.reportingPeriod': 'P\u00e9riode',
  'table.createdAt': 'Cr\u00e9\u00e9',
  'table.actions': 'Actions',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'Taxonomie UE',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Compl\u00e9tude des donn\u00e9es',
  'compliance.nextDeadline': 'Prochaine \u00e9ch\u00e9ance',

  // Reports
  'report.quarterly': 'Rapport trimestriel',
  'report.annual': 'Rapport annuel',
  'report.paiStatement': 'D\u00e9claration PAI',
  'report.taxonomyReport': 'Rapport taxonomie',
  'report.generatedAt': 'G\u00e9n\u00e9r\u00e9',
  'report.version': 'Version',

  // General
  'general.loading': 'Chargement...',
  'general.noData': 'Aucune donn\u00e9e disponible',
  'general.error': 'Une erreur est survenue',
  'general.retry': 'R\u00e9essayer',
  'general.total': 'Total',
  'general.page': 'Page',
  'general.of': 'sur',
  'general.showing': 'Affichage',
  'general.results': 'r\u00e9sultats',
  'general.language': 'Langue',
  'general.english': 'Anglais',
  'general.french': 'Fran\u00e7ais',
};
